/**
 * UI Selectors
 *
 * Advanced selectors for UI state that involve cross-slice logic.
 * These selectors compute derived state from multiple UI slices.
 *
 * @module store/selectors/uiSelectors
 */

import { getAllArtifacts } from '@/artifacts';
import type { GeoMarkupStore } from '../index';

/**
 * Returns true when any artifact has a panel open (form or details).
 * Registry-driven — automatically covers new artifact types.
 */
export const selectIsAnyArtifactPanelOpen = (state: GeoMarkupStore): boolean =>
  getAllArtifacts().some((artifact) => {
    const slice = artifact.getSlice(state);
    return slice.formIsOpen || slice.detailsIsOpen;
  });

/**
 * Returns true when any artifact has a form open (create or edit).
 * Registry-driven — automatically covers new artifact types.
 */
export const selectIsAnyArtifactFormOpen = (state: GeoMarkupStore): boolean =>
  getAllArtifacts().some((artifact) => artifact.getSlice(state).formIsOpen);

/**
 * Shared guard for the two "hide on mobile" selectors.
 * Returns `true` (hide unconditionally), `false` (show unconditionally),
 * or `null` (let the caller decide based on panel expansion state).
 */
const resolveMobileHideGuard = (ui: GeoMarkupStore['ui']): boolean | null => {
  if (ui.toolbarGuide.isOpen) return true;
  if (ui.geometriesList.isOpen) return true;
  if (ui.openSlidePanelCount === 0) return false;
  // A second SlidePanel (layers, configurator, related tasks) stacked on top of
  // a reduced artifact panel is always fully expanded — hide chrome regardless
  // of the underlying panel's stale isMinimized/previewMode flags.
  if (ui.openSlidePanelCount > 1) return true;
  return null;
};

/**
 * Returns true when UI elements should be hidden on mobile.
 *
 * Hides UI when:
 * - Toolbar guide is open, OR
 * - GeometriesList is open (doesn't support minimize), OR
 * - More than one SlidePanel is open (the topmost is always fully expanded), OR
 * - A single SlidePanel is open AND fully expanded (not minimized AND not preview)
 *
 * Driven by the generic `openSlidePanelCount` counter, so every SlidePanel
 * (artifact forms/details, LayersPanel, LayerConfiguratorPanel, RelatedTasksListPanel, …)
 * automatically hides chrome on mobile.
 *
 * Used by: AppShell, MapContainer, MapControls, MapToolbox, DrawingInstructions, SlidePanel.
 */
export const selectShouldHideUIOnMobile = (state: GeoMarkupStore): boolean => {
  const guard = resolveMobileHideGuard(state.ui);
  if (guard !== null) return guard;
  return !state.ui.slidePanel.isMinimized && !state.ui.slidePanel.previewMode;
};

/**
 * Returns true when GuideCarousel should be hidden on mobile.
 *
 * Same conditions as `selectShouldHideUIOnMobile` except also hides in
 * preview mode (so carousel disappears as soon as the panel is non-minimized).
 *
 * Used by: GuideCarousel only.
 */
export const selectShouldHideCarouselOnMobile = (
  state: GeoMarkupStore
): boolean => {
  const guard = resolveMobileHideGuard(state.ui);
  if (guard !== null) return guard;
  return !state.ui.slidePanel.isMinimized;
};
